export const humanFileSize = (bytes: number, si = false, dp = 1) => {
	const thresh = si ? 1000 : 1024;

	if (Math.abs(bytes) < thresh) {
		return bytes + " B";
	}

	const units = si ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"] : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
	let u = -1;
	const r = 10 ** dp;

	do {
		bytes /= thresh;
		++u;
	} while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

	return bytes.toFixed(dp) + " " + units[u];
};

export const handleDownload = async (file: { mimetype: string; name: string; target_id: number; target_type: string; res: any }) => {
	let blob: Blob | undefined = undefined;

	if (file.res instanceof ArrayBuffer || file.res instanceof Uint8Array) {
		//  Already binary buffer
		//!TODO: remove any
		blob = new Blob([file.res as any], { type: file.mimetype });
	} else if (typeof file.res === "string") {
		if (file.mimetype.startsWith("text/") || file.mimetype.includes("xml") || file.mimetype.includes("svg")) {
			// Treat as text
			blob = new Blob([file.res], { type: file.mimetype });
		} else {
			//  Treat string as binary data
			const byteArray = new TextEncoder().encode(file.res);
			blob = new Blob([byteArray], { type: file.mimetype });
		}
	} else {
		// Fallback: serialize unknown types to JSON/string so blob is always assigned
		const data = typeof file.res === "object" ? JSON.stringify(file.res) : String(file.res);
		blob = new Blob([data], { type: file.mimetype || "application/octet-stream" });
	}

	// Create download link
	//!TODO: remove any
	if (blob) {
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", file.name);
		document.body.appendChild(link);
		link.click();

		// Cleanup
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	}
};

const DEFAULT_CARD_FORMAT = /(\d{1,4})/g;

const CARD_TYPES = [
	{
		displayName: "Visa",
		type: "visa",
		format: DEFAULT_CARD_FORMAT,
		startPattern: /^4/,
		gaps: [4, 8, 12],
		lengths: [16, 18, 19],
		code: {
			name: "CVV",
			length: 3,
		},
	},
	{
		displayName: "Mastercard",
		type: "mastercard",
		format: DEFAULT_CARD_FORMAT,
		startPattern: /^(5[1-5]|677189)|^(222[1-9]|2[3-6]\d{2}|27[0-1]\d|2720)/,
		gaps: [4, 8, 12],
		lengths: [16],
		code: {
			name: "CVC",
			length: 3,
		},
	},
	{
		displayName: "American Express",
		type: "amex",
		format: /(\d{1,4})(\d{1,6})?(\d{1,5})?/,
		startPattern: /^3[47]/,
		gaps: [4, 10],
		lengths: [15],
		code: {
			name: "CID",
			length: 4,
		},
	},
	{
		displayName: "Diners Club",
		type: "dinersclub",
		format: DEFAULT_CARD_FORMAT,
		startPattern: /^(36|38|30[0-5])/,
		gaps: [4, 10],
		lengths: [14, 16, 19],
		code: {
			name: "CVV",
			length: 3,
		},
	},
	{
		displayName: "Discover",
		type: "discover",
		format: DEFAULT_CARD_FORMAT,
		startPattern: /^(6011|65|64[4-9]|622)/,
		gaps: [4, 8, 12],
		lengths: [16, 19],
		code: {
			name: "CID",
			length: 3,
		},
	},
	{
		displayName: "JCB",
		type: "jcb",
		format: DEFAULT_CARD_FORMAT,
		startPattern: /^35/,
		gaps: [4, 8, 12],
		lengths: [16, 17, 18, 19],
		code: {
			name: "CVV",
			length: 3,
		},
	},
	{
		displayName: "UnionPay",
		type: "unionpay",
		format: DEFAULT_CARD_FORMAT,
		startPattern: /^62/,
		gaps: [4, 8, 12],
		lengths: [14, 15, 16, 17, 18, 19],
		code: {
			name: "CVN",
			length: 3,
		},
	},
	{
		displayName: "Maestro",
		type: "maestro",
		format: DEFAULT_CARD_FORMAT,
		startPattern: /^(5018|5020|5038|6304|6703|6708|6759|676[1-3])/,
		gaps: [4, 8, 12],
		lengths: [12, 13, 14, 15, 16, 17, 18, 19],
		code: {
			name: "CVC",
			length: 3,
		},
	},
	{
		displayName: "Elo",
		type: "elo",
		format: DEFAULT_CARD_FORMAT,
		startPattern:
			/^(4011(78|79)|43(1274|8935)|45(1416|7393|763(1|2))|50(4175|6699|67[0-7][0-9]|9000)|627780|63(6297|6368)|650(03([^4])|04([0-9])|05(0|1)|4(0[5-9]|3[0-9]|8[5-9]|9[0-9])|5([0-2][0-9]|3[0-8])|9([2-6][0-9]|7[0-8])|541|700|720|901)|651652|655000|655021)/,
		gaps: [4, 8, 12],
		lengths: [16],
		code: {
			name: "CVE",
			length: 3,
		},
	},
	{
		displayName: "Hipercard",
		type: "hipercard",
		format: DEFAULT_CARD_FORMAT,
		startPattern: /^(384100|384140|384160|606282|637095|637568|60(?!11))/,
		gaps: [4, 8, 12],
		lengths: [16],
		code: {
			name: "CVC",
			length: 3,
		},
	},
];

export const getCardType = (
	value: string
): "unknown" | "visa" | "mastercard" | "amex" | "dinersclub" | "discover" | "jcb" | "unionpay" | "maestro" | "elo" | "hipercard" => {
	const find = CARD_TYPES.filter(function (cardType) {
		return cardType.startPattern.test(value);
	});
	if (find && find.length) {
		return find[0].type as any;
	} else {
		return "unknown";
	}
};

export const toBase64 = (dataSource: string, stringify: boolean) => {
	const jsonStr = stringify ? JSON.stringify(dataSource) : dataSource;
	const utf8Bytes = new TextEncoder().encode(jsonStr);
	let binary = "";
	utf8Bytes.forEach(b => (binary += String.fromCharCode(b)));
	const base = btoa(binary);
	return base;
};

export const base64ToBlob = (base64: string) => {
	let binaryString = window.atob(base64);
	let binaryLen = binaryString.length;

	let ab = new ArrayBuffer(binaryLen);
	let ia = new Uint8Array(ab);
	for (let i = 0; i < binaryLen; i++) {
		ia[i] = binaryString.charCodeAt(i);
	}

	let bb = new Blob([ab]);

	// const isPNG = check([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
	// const realFileElement = document.querySelector("#realFileType");async function handleChange(event) {
	//   const file = event.target.files[0];
	//   const buffers = await readBuffer(file, 0, 8);
	//   const uint8Array = new Uint8Array(buffers);
	//   realFileElement.innerText = `The type of ${file.name} is：${
	//     isPNG(uint8Array) ? "image/png" : file.type
	//   }`;
	// }

	//bb.lastModifiedDate = new Date();
	//bb.name = "archive.zip";
	//bb.type = "zip";

	return bb;
};

export const mergeObject = (target: any, source: any): void => {
	if (typeof target !== "object" || target === null) {
		return;
	}

	for (const key in source) {
		const sourceValue = source[key];
		if (sourceValue === undefined || sourceValue === null) {
			continue;
		}
		if (Array.isArray(sourceValue) && Array.isArray(target[key])) {
			const ruleTemplate = target[key][0];

			target[key] = sourceValue.map((item: any) => {
				const newRule = structuredClone(ruleTemplate);
				mergeObject(newRule, item);
				return newRule;
			});
			continue;
		}

		if (typeof sourceValue === "object" && !Array.isArray(sourceValue) && typeof target[key] === "object" && !Array.isArray(target[key])) {
			mergeObject(target[key], sourceValue);
			continue;
		}
		target[key] = sourceValue;
	}
};
